
import { useState, useMemo } from "react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowUpDown, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import type { FBPost, IGPost } from "@/lib/types";
import { ImagePreview } from "@/components/cards/ImagePreview";
import { getPostTypeLabel } from "@/lib/post-type-labels";

interface PostsTableProps {
  posts: (FBPost | IGPost)[];
  platform: "facebook" | "instagram";
  pageSize?: number;
}

const typeBadgeColor: Record<string, string> = {
  photo: "bg-blue-100 text-blue-800",
  IMAGE: "bg-blue-100 text-blue-800",
  video: "bg-purple-100 text-purple-800",
  VIDEO: "bg-purple-100 text-purple-800",
  CAROUSEL_ALBUM: "bg-amber-100 text-amber-800",
  link: "bg-gray-100 text-gray-800",
  status: "bg-gray-100 text-gray-800",
  REELS: "bg-pink-100 text-pink-800",
  FEED: "bg-green-100 text-green-800",
};

function fmt(n: number) {
  return n.toLocaleString("cs-CZ");
}

function getPostImage(post: FBPost | IGPost, platform: string): string | null {
  if (platform === "instagram") {
    const ig = post as IGPost;
    return ig.media_thumbnail_url || ig.media_url || null;
  }
  return (post as FBPost).post_picture || null;
}

function getPostType(post: FBPost | IGPost, platform: string): string | null {
  if (platform === "facebook") return (post as FBPost).type;
  return (post as IGPost).media_product_type || (post as IGPost).media_type;
}

function SortHeader({ label, field, onSort }: { label: string; field: string; onSort: (field: string) => void }) {
  return (
    <TableHead>
      <button onClick={() => onSort(field)} className="flex items-center gap-1 hover:text-foreground cursor-pointer">
        {label}<ArrowUpDown className="h-3 w-3" />
      </button>
    </TableHead>
  );
}

export function PostsTable({ posts, platform, pageSize = 10 }: PostsTableProps) {
  const [sortKey, setSortKey] = useState<string>("date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(0);

  const sorted = useMemo(() => {
    return [...posts].sort((a, b) => {
      const aVal = (a as unknown as Record<string, unknown>)[sortKey];
      const bVal = (b as unknown as Record<string, unknown>)[sortKey];
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }
      return sortDir === "asc"
        ? String(aVal ?? "").localeCompare(String(bVal ?? ""))
        : String(bVal ?? "").localeCompare(String(aVal ?? ""));
    });
  }, [posts, sortKey, sortDir]);

  const paged = sorted.slice(page * pageSize, (page + 1) * pageSize);
  const totalPages = Math.ceil(posts.length / pageSize);

  const handleSort = (key: string) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
    setPage(0);
  };

  if (platform === "facebook") {
    const fbPosts = paged as FBPost[];
    return (
      <div>
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[56px] min-w-[56px]"></TableHead>
                <SortHeader onSort={handleSort} label="Datum" field="date" />
                <TableHead>Účet</TableHead>
                <TableHead>Typ</TableHead>
                <TableHead className="max-w-[200px]">Text</TableHead>
                <SortHeader onSort={handleSort} label="Dosah" field="post_impressions_unique" />
                <SortHeader onSort={handleSort} label="Engagement" field="page_post_engagements" />
                <SortHeader onSort={handleSort} label="Reakce" field="post_reactions_total" />
                <SortHeader onSort={handleSort} label="Komentáře" field="post_comments_total" />
                <SortHeader onSort={handleSort} label="Sdílení" field="post_activity_by_action_type_share" />
                <SortHeader onSort={handleSort} label="Kliky" field="post_clicks" />
                <SortHeader onSort={handleSort} label="Video" field="post_video_views" />
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fbPosts.map((p, i) => (
                <TableRow key={p.post_id || `fb-${i}`}>
                  <TableCell><ImagePreview src={getPostImage(p, "facebook")} fallbackType={getPostType(p, "facebook")} /></TableCell>
                  <TableCell className="whitespace-nowrap">{p.date ? new Date(p.date).toLocaleDateString("cs-CZ") : "—"}</TableCell>
                  <TableCell className="whitespace-nowrap">{p.account_name}</TableCell>
                  <TableCell>
                    {p.type && <Badge variant="secondary" className={typeBadgeColor[p.type] || ""}>{getPostTypeLabel(p.type)}</Badge>}
                  </TableCell>
                  <TableCell className="max-w-[250px] truncate" title={p.post_message || ""}>{p.post_message || "—"}</TableCell>
                  <TableCell>{fmt(p.post_impressions_unique)}</TableCell>
                  <TableCell className="font-medium">{fmt(p.page_post_engagements)}</TableCell>
                  <TableCell>{fmt(p.post_reactions_total)}</TableCell>
                  <TableCell>{fmt(p.post_comments_total)}</TableCell>
                  <TableCell>{fmt(p.post_activity_by_action_type_share)}</TableCell>
                  <TableCell>{fmt(p.post_clicks)}</TableCell>
                  <TableCell>{p.post_video_views > 0 ? fmt(p.post_video_views) : "—"}</TableCell>
                  <TableCell>
                    {p.permalink_url && (
                      <a href={p.permalink_url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground">
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <Pagination page={page} setPage={setPage} pageSize={pageSize} total={posts.length} totalPages={totalPages} />
      </div>
    );
  }

  // Instagram
  const igPosts = paged as IGPost[];
  return (
    <div>
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[56px] min-w-[56px]"></TableHead>
              <SortHeader onSort={handleSort} label="Datum" field="date" />
              <TableHead>Účet</TableHead>
              <TableHead>Typ</TableHead>
              <TableHead className="max-w-[200px]">Popisek</TableHead>
              <SortHeader onSort={handleSort} label="Dosah" field="media_reach" />
              <SortHeader onSort={handleSort} label="Engagement" field="media_engagement" />
              <SortHeader onSort={handleSort} label="Lajky" field="media_like_count" />
              <SortHeader onSort={handleSort} label="Komentáře" field="media_comments_count" />
              <SortHeader onSort={handleSort} label="Uložení" field="media_saved" />
              <SortHeader onSort={handleSort} label="Sdílení" field="media_shares" />
              <SortHeader onSort={handleSort} label="Přehrání" field="media_plays" />
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {igPosts.map((p, i) => (
              <TableRow key={p.media_id || `ig-${i}`}>
                <TableCell><ImagePreview src={getPostImage(p, "instagram")} fallbackType={getPostType(p, "instagram")} /></TableCell>
                <TableCell className="whitespace-nowrap">{p.date ? new Date(p.date).toLocaleDateString("cs-CZ") : "—"}</TableCell>
                <TableCell className="whitespace-nowrap">{p.account_name}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {p.media_type && <Badge variant="secondary" className={typeBadgeColor[p.media_type] || ""}>{getPostTypeLabel(p.media_type)}</Badge>}
                    {p.media_product_type && p.media_product_type !== p.media_type && (
                      <Badge variant="outline" className={typeBadgeColor[p.media_product_type] || ""}>{getPostTypeLabel(p.media_product_type)}</Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="max-w-[250px] truncate" title={p.media_caption || ""}>{p.media_caption || "—"}</TableCell>
                <TableCell>{fmt(p.media_reach)}</TableCell>
                <TableCell className="font-medium">{fmt(p.media_engagement)}</TableCell>
                <TableCell>{fmt(p.media_like_count)}</TableCell>
                <TableCell>{fmt(p.media_comments_count)}</TableCell>
                <TableCell>{fmt(p.media_saved)}</TableCell>
                <TableCell>{fmt(p.media_shares)}</TableCell>
                <TableCell>{p.media_plays > 0 ? fmt(p.media_plays) : "—"}</TableCell>
                <TableCell>
                  {p.media_permalink && (
                    <a href={p.media_permalink} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground">
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Pagination page={page} setPage={setPage} pageSize={pageSize} total={posts.length} totalPages={totalPages} />
    </div>
  );
}

function Pagination({ page, setPage, pageSize, total, totalPages }: {
  page: number; setPage: (p: number) => void; pageSize: number; total: number; totalPages: number;
}) {
  return (
    <div className="flex items-center justify-between mt-4">
      <p className="text-sm text-muted-foreground">
        Zobrazeno {page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} z {total}
      </p>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 0}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
